/*global CustomFunctions */
import { isGuid, formulaErrorHandling } from "../../shared/helpers";
import { FormulaErrorFormat } from "../../shared/types";
import { ASSET_BY_ASSET_ID_BATCHER } from "../../shared/batchers";
import { observerForCustomFunction } from "../../shared/query-client";

/**
 * Gets DDB asset name.
 * @customfunction ASSET.NAME
 * @param assetId The asset ID.
 * @returns The asset name.
 * @streaming
 */
export function assetName(assetId: string, invocation: CustomFunctions.StreamingInvocation<string>): void {
  if (!isGuid(assetId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.ASSET_ID));
    return;
  }

  observerForCustomFunction(invocation, {
    queryKey: ["asset", assetId],
    queryFn: async () => ASSET_BY_ASSET_ID_BATCHER.fetch(assetId),
    select: ({ name }) => name,
  });
}

/**
 * Gets DDB asset parent.
 * @customfunction ASSET.PARENT_ID
 * @param assetId The asset ID.
 * @returns The asset name.
 * @streaming
 */
export function parentAssetId(assetId: string, invocation: CustomFunctions.StreamingInvocation<string>): void {
  if (!isGuid(assetId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.ASSET_ID));
    return;
  }

  observerForCustomFunction(invocation, {
    queryKey: ["asset", assetId],
    queryFn: async () => ASSET_BY_ASSET_ID_BATCHER.fetch(assetId),
    select: ({ parent }) => parent ?? "",
  });
}

/**
 * Gets DDB asset children.
 * @customfunction ASSET.CHILD_IDS
 * @param assetId The asset ID.
 * @returns The asset child IDs.
 * @streaming
 */
export function childIds(assetId: string, invocation: CustomFunctions.StreamingInvocation<string[][]>): void {
  if (!isGuid(assetId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.ASSET_ID));
    return;
  }

  observerForCustomFunction(invocation, {
    queryKey: ["asset", assetId],
    queryFn: async () => ASSET_BY_ASSET_ID_BATCHER.fetch(assetId),
    select: ({ children }) => children?.map((id) => [id]) ?? [],
  });
}

/**
 * Gets DDB asset type ID.
 * @customfunction ASSET.ASSET_TYPE_ID
 * @param assetId The asset ID.
 * @returns The asset type ID.
 * @streaming
 */
export function assetTypeId(assetId: string, invocation: CustomFunctions.StreamingInvocation<string>): void {
  if (!isGuid(assetId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.ASSET_ID));
    return;
  }

  observerForCustomFunction(invocation, {
    queryKey: ["asset", assetId],
    queryFn: async () => ASSET_BY_ASSET_ID_BATCHER.fetch(assetId),
    select: ({ asset_type }) => asset_type.id,
  });
}

/**
 * Gets DDB asset type name.
 * @customfunction ASSET.ASSET_TYPE_NAME
 * @param assetId The asset ID.
 * @returns The asset type name.
 * @streaming
 */
export function assetTypeName(assetId: string, invocation: CustomFunctions.StreamingInvocation<string>): void {
  if (!isGuid(assetId)) {
    invocation.setResult(formulaErrorHandling(FormulaErrorFormat.ASSET_ID));
    return;
  }

  observerForCustomFunction(invocation, {
    queryKey: ["asset", assetId],
    queryFn: async () => ASSET_BY_ASSET_ID_BATCHER.fetch(assetId),
    select: ({ asset_type }) => asset_type.name,
  });
}

CustomFunctions.associate("ASSET.NAME", assetName);
CustomFunctions.associate("ASSET.PARENT_ID", parentAssetId);
CustomFunctions.associate("ASSET.CHILD_IDS", childIds);
CustomFunctions.associate("ASSET.ASSET_TYPE_ID", assetTypeId);
CustomFunctions.associate("ASSET.ASSET_TYPE_NAME", assetTypeName);